import { A11yModule } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	HostListener,
	inject,
	OnInit,
	signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TestHookDirective } from '@core/ui';
import {
	MatterPackage,
	MatterPackageType,
	matterPackageTypeKeys,
} from '@models/matter-package';
import { MatterWordDocument } from '@models/matter-word-documents';
import { dispatch, select, Store } from '@ngxs/store';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { BusinessFeatureFlagSelectors, BusinessState } from '@state/business';
import { BusinessBaseDocxsState } from '@state/business/word/business-base-docxs/business-base-docxs.state';
import { MatterPackagesHistoryState } from '@state/matter/matter-packages-history/matter-packages-history.state';
import { MatterPackagesDispatch } from '@state/matter/matter-packages/matter-packages.dispatch';
import { MatterWordDocumentsDispatch } from '@state/matter/matter-word-documents/matter-word-documents.dispatch';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { EnvelopeService } from 'src/app/shared/services/envelope/envelope.service';
import { ExportPackage } from 'src/app/shared/state/matter/matter-packages/matter-packages.actions';
import { ButtonComponent } from 'src/app/ui/button/button.component';
import {
	AnalyticsContext,
	DocListItemComponent,
} from 'src/app/ui/doc-list-item/doc-list-item.component';
import {
	DropdownComponent,
	DropdownItem,
} from 'src/app/ui/dropdown/dropdown.component';
import { ColSpanDirective } from 'src/app/ui/grid/col-span.directive';
import { IconsModule } from 'src/app/ui/icons/icons.module';
import { ListItemComponent } from 'src/app/ui/list-item/list-item.component';
import { ModalService } from 'src/app/ui/modal/modal.service';
import { UI_TYPES } from 'src/app/ui/modal/modal.types';
import { MatterPackageWithStatusAndDocuments } from '../documents-v2.selectors';
import { MatterPackageTypeComponent } from './components/matter-package-type/matter-package-type.component';
import { MatterPackageHistoryButtonComponent } from './components/matter-packages-history-button/matter-package-history-button.component';
import { TestHooks } from './matter-packages.hooks';
import { MatterPackagesSelectors } from './matter-packages.selectors';
import { PackageIconPipe } from './package-icon.pipe';
import { StatusComponent } from 'src/app/ui/status/status.component';
import { STATUSES } from '@models/statuses';
import {
	MatterPackagesViewModel,
	SortDirection,
	SortField,
} from './matter-packages.view-model';
import { ESigningStatusActionsDropdownComponent } from '../../documents/envelopes/common/e-signing-status-actions-dropdown/e-signing-status-actions-dropdown.component';
import { MatterPackagesService } from 'src/app/shared/services/matter-packages/matter-packages.service';

@Component({
	selector: 'app-matter-packages',
	templateUrl: './matter-packages.component.html',
	imports: [
		A11yModule,
		CommonModule,
		IconsModule,
		ButtonComponent,
		ColSpanDirective,
		DropdownComponent,
		FormsModule,
		DocListItemComponent,
		ListItemComponent,
		TestHookDirective,
		MatterPackageHistoryButtonComponent,
		MatterPackageTypeComponent,
		PackageIconPipe,
		StatusComponent,
		ESigningStatusActionsDropdownComponent,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [MatterPackagesViewModel],
})
export class MatterPackagesComponent implements OnInit {
	readonly viewModel = inject(MatterPackagesViewModel);
	testHooks = TestHooks;
	public STATUSES = STATUSES;
	public matterId_ = select(SelectedMatter.id);

	@HostListener('window:focus')
	async onWindowFocus() {
		await this.envelopeService.getEnvelopeViewModels(this.matterId_());
	}

	private ExportPackage = dispatch(ExportPackage);
	public envelopeService = inject(EnvelopeService);
	public MatterPackageType = MatterPackageType;
	public matterName = this.store.selectSnapshot(SelectedMatter.matterName);
	public packageHistoryEntries$ = this.store.select(
		MatterPackagesHistoryState.getAllPackageHistoryEntries,
	);
	public wordDocumentStyles = this.store.selectSnapshot(
		BusinessState.getBusinessWordDocumentStyles,
	);
	public officeId = this.store.selectSnapshot(SelectedMatter.officeId);
	public getBaseDocxPathForOfficeFn = this.store.selectSnapshot(
		BusinessBaseDocxsState.getBaseDocxPathForOffice,
	);
	public editingNamePackageId_ = signal<string | null>(null);
	protected readonly isSigningPackageKey =
		matterPackageTypeKeys.IsSigningPackage;
	private matterPackagesService = inject(MatterPackagesService);

	isSuggestedPackagesEnabled_ = select(
		BusinessFeatureFlagSelectors.isSuggestedPackagesEnabled,
	);

	packageResults_ = select(
		MatterPackagesSelectors.getAllPackagesWithStatusesAndDocuments,
	);
	envelopes_ = computed(() => {
		return this.envelopeService.envelopeConfigs_();
	});

	mergedPackagesWithEnvelopes_ = computed(() => {
		const matterPackages = this.packageResults_()?.matterPackages ?? [];
		const envelopes = this.envelopes_() ?? [];

		const packagesWithEnvelopes = matterPackages.map((pkg) => {
			const matchingEnvelope = envelopes.find(
				(envelope) => envelope.id === pkg.id,
			);
			return {
				...pkg,
				envelope: matchingEnvelope || null,
			};
		});

		return this.viewModel.sortPackages(packagesWithEnvelopes);
	});

	public isSignItEnabled_ = select(
		BusinessFeatureFlagSelectors.sc16163EnableSignIt,
	);

	newPackageName = '';

	public packageDropdownItems_ = computed(() => {
		const packagesWithEnvelopes = this.mergedPackagesWithEnvelopes_();
		return packagesWithEnvelopes.reduce(
			(acc, pkg) => {
				acc[pkg.id] = [
					{
						id: 'export',
						title: {
							label: 'Export as PDF',
							icon: 'file-type-pdf',
						},
						action: () => {
							this.exportPackage(pkg.id);
						},
					},
					{
						id: 'rename',
						title: { label: 'Rename', icon: 'cursor-text' },
						action: () => {
							this.toggleEditingName(pkg.id);
						},
					},
					{
						id: 'delete',
						title: {
							label: 'Delete',
							icon: 'trash',
						},
						type: UI_TYPES.DANGER,
						action: () => {
							this.deletePackage(pkg.id);
						},
					},
				];
				return acc;
			},
			{} as Record<string, DropdownItem[]>,
		);
	});

	public documentDropdownItems_ = computed(() => {
		const matterPackages = this.packageResults_();
		return (
			matterPackages?.matterPackages?.flatMap((pkg) =>
				(pkg.documents ?? []).map((doc) => ({ doc, packageId: pkg.id })),
			) ?? []
		).reduce(
			(acc, { doc, packageId }) => ({
				...acc,
				[doc.id]: [
					{
						id: 'remove',
						title: {
							label: 'Remove',
							icon: 'minus',
						},
						action: () =>
							this.matterPackagesDispatch.RemoveContent({
								packageId,
								contentId: doc.id,
							}),
					},
				],
			}),
			{},
		);
	});

	private expandedPackages_ = signal<Record<string, boolean>>({});
	private readonly STORAGE_KEY = 'matter-packages-expanded';
	private readonly DOC_STORAGE_KEY = 'matter-packages-selected-doc-id';
	readonly ANALYTIC_CONTEXT = AnalyticsContext;

	constructor(
		private matterPackagesDispatch: MatterPackagesDispatch,
		private modalService: ModalService,
		private matterDocumentDispatch: MatterWordDocumentsDispatch,
		private analytics: AnalyticsService,
		private store: Store,
	) {
		effect(() => {
			const matterPackages = this.packageResults_()?.matterPackages;
			const virtualPackages = this.packageResults_()?.allVirtualPackages;
			this.syncPackagesIfNeeded(matterPackages, virtualPackages);
		});

		effect(() => {
			const packageId = this.editingNamePackageId_();
			if (packageId) {
				setTimeout(() => {
					const input = document.getElementById(
						`package-name-input-${packageId}`,
					) as HTMLInputElement;
					if (input) {
						input.focus();
						input.select();
					}
				}, 0);
			}
		});
	}

	private syncPackagesIfNeeded(
		matterPackages: MatterPackageWithStatusAndDocuments[],
		virtualPackages: MatterPackageWithStatusAndDocuments[],
	) {
		if (!matterPackages || !virtualPackages) {
			return;
		}

		const virtualPackagesByHash = new Map(
			virtualPackages.map((pkg) => [pkg.packageHash || pkg.id, pkg]),
		);

		matterPackages.forEach((matterPkg) => {
			if (!matterPkg.packageHash) {
				return;
			}

			const virtualPkg = virtualPackagesByHash.get(matterPkg.packageHash);
			if (!virtualPkg) {
				return;
			}

			this.matterPackagesDispatch.SyncPackageWithVirtual({
				matterPackageId: matterPkg.id,
				virtualPackageHash: virtualPkg.packageHash,
				virtualContents: virtualPkg.contents,
				virtualDocuments: virtualPkg.documents.map((doc) => ({
					...doc,
					id: doc.document.id,
				})),
			});
		});
	}

	ngOnInit() {
		const savedExpandedState = JSON.parse(
			localStorage.getItem(this.STORAGE_KEY) || '{}',
		);
		this.expandedPackages_.set(savedExpandedState);
		this.onWindowFocus();
	}

	private async createDynamicPackage(
		dynamicPackage: MatterPackageWithStatusAndDocuments,
	): Promise<string> {
		return new Promise((resolve, reject) => {
			this.matterPackagesDispatch.Create({
				packageName: dynamicPackage.packageName,
				documents: dynamicPackage.documents,
				packageTemplateId:
					dynamicPackage.packageTemplateId || dynamicPackage.id,
				packageHash: dynamicPackage.id,
			});

			// Since we don't have a direct way to get the created package ID from the action,
			// we'll need to listen for the state change and find the newly created package
			// For now, we'll use a timeout approach as a temporary solution
			setTimeout(() => {
				const allPackages = this.packageResults_()?.matterPackages;
				const newPackage = allPackages?.find(
					(pkg) => pkg.packageHash === dynamicPackage.id,
				);

				if (newPackage) {
					resolve(newPackage.id);
				} else {
					reject(new Error('Failed to create dynamic package'));
				}
			}, 1000); // Wait for state to update
		});
	}

	async gotoEditPackage(packageId: string, docId?: string, openDrawer = false) {
		let actualPackageId = packageId;
		// Check if this is a dynamic package that needs to be created first
		if (
			!this.packageResults_()?.matterPackages?.find(
				(pkg) => pkg.id === packageId,
			)
		) {
			const dynamicPackage = this.packageResults_()?.allVirtualPackages?.find(
				(pkg) => pkg.id === packageId,
			);

			if (dynamicPackage) {
				try {
					// Create the dynamic package and get the real package ID
					actualPackageId = await this.createDynamicPackage(dynamicPackage);
				} catch (error) {
					console.error('Failed to create dynamic package:', error);
					return; // Don't navigate if package creation failed
				}
			}
		}

		if (docId) {
			localStorage.setItem(this.DOC_STORAGE_KEY, docId);
		} else {
			localStorage.removeItem(this.DOC_STORAGE_KEY);
		}

		if (openDrawer) {
			localStorage.setItem('matter-packages-open-drawer', 'true');
		}

		this.envelopeService.goToEnvelopeOrPackageHandler(
			this.matterId_(),
			actualPackageId,
		);
	}

	onOpenDocumentInWord(doc: MatterWordDocument) {
		this.matterDocumentDispatch.OpenDocumentInWord(doc);
	}

	updatePackageName(packageId: string, newName: string) {
		this.analytics.logEvent(AnalyticsEvent.MATTER_PACKAGE_KEBAB_RENAME);

		const signingPackage = this.mergedPackagesWithEnvelopes_().find(
			(p) => p.id === packageId && p.packageTypes?.isSigningPackage,
		);

		signingPackage
			? this.envelopeService.renameEnvelope(packageId, newName)
			: this.matterPackagesDispatch.UpdatePackageName({
					id: packageId,
					packageName: newName,
				});
	}

	createPackage() {
		this.matterPackagesDispatch.Create({
			packageName: this.newPackageName,
		} as MatterPackage);
		this.newPackageName = '';
	}

	exportPackage(packageId: string) {
		this.analytics.logEvent(AnalyticsEvent.MATTER_PACKAGE_EXPORT);
		this.ExportPackage(packageId);
	}

	deletePackage(packageId: string) {
		this.matterPackagesService.deleteMatterPackage(
			packageId,
			this.matterId_(),
			AnalyticsEvent.MATTER_PACKAGE_KEBAB_DELETE,
		);
	}

	downloadEnvelope(packageId: string) {
		this.envelopeService.downloadEnvelope(packageId);
	}

	cancelEnvelope(packageId: string) {
		this.envelopeService.openPopConfirmForCancel(this.matterId_(), packageId);
	}

	toggleExpand(pkg: MatterPackage) {
		this.analytics.logEvent(AnalyticsEvent.MATTER_PACKAGE_CHEVRON);
		const currentExpandedState = this.expandedPackages_();
		const newExpandedState = {
			...currentExpandedState,
			[pkg.id]: !currentExpandedState[pkg.id],
		};

		// Remove the key entirely if it's false to keep the object clean
		if (!newExpandedState[pkg.id]) {
			delete newExpandedState[pkg.id];
		}

		this.expandedPackages_.set(newExpandedState);

		if (Object.keys(newExpandedState).length > 0) {
			localStorage.setItem(this.STORAGE_KEY, JSON.stringify(newExpandedState));
		} else {
			localStorage.removeItem(this.STORAGE_KEY);
		}
	}

	public toggleEditingName(packageId: string | null) {
		this.editingNamePackageId_.set(packageId);
	}

	onPackageNameBlur(packageId: string, originalName: string, newName: string) {
		const trimmedName = newName.trim();
		if (trimmedName !== '' && trimmedName !== originalName) {
			this.updatePackageName(packageId, trimmedName);
		}
		this.toggleEditingName(null);
	}

	onPackageNameEnter(packageId: string, originalName: string, newName: string) {
		const trimmedName = newName.trim();
		if (trimmedName !== '' && trimmedName !== originalName) {
			this.updatePackageName(packageId, trimmedName);
		}
		this.toggleEditingName(null);
	}

	trackByFn(index: number, item: { id?: string }): string | number {
		return item.id || index;
	}

	onSort(field: SortField): void {
		this.viewModel.toggleSort(field);
	}

	getSortDirection(field: SortField): SortDirection {
		return this.viewModel.sortDirections()[field];
	}

	getSortIcon(field: SortField): string {
		return this.viewModel.sortIcons()[field];
	}
}
